import React, { useState, useEffect } from 'react';
import { StyleSheet, Text, View, ScrollView, Dimensions, TouchableOpacity } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import Data from './Data';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from 'recharts';
import moment from 'moment';
import { Picker } from '@react-native-picker/picker';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const { width, height } = Dimensions.get('window');

export default function SensorUseDetail({ route, navigation }: any) {
    const [__p] = useState(() => {
        if (typeof window === 'undefined') return route?.params || {};
        try {
            const __k = '_navParams_SensorUseDetail';
            if (route?.params?.data != null) {
                window.localStorage.setItem(__k, JSON.stringify(route.params));
                return route.params;
            }
            const __s = window.localStorage.getItem(__k);
            if (__s) {
                const parsed = JSON.parse(__s);
                setTimeout(() => navigation.setParams(parsed), 0);
                return parsed;
            }
        } catch {}
        return route?.params || {};
    });
    const data = __p?.data || {};
    const isFocused = useIsFocused();

    const [daily, setDaily] = useState<any>(null);
    const [month, setMonth] = useState<any>(null);
    const [year, setYear] = useState<any>(null);
    const [info, setInfo] = useState<any>(null);

    const [weeklyData, setWeeklyData] = useState<any[]>([]);
    const [monthlyData, setMonthlyData] = useState<any[]>([]);
    const [search, setSearch] = useState<any[]>([]);
    const [xTickFontSize, setXTickFontSize] = useState(16);
    const [graphSize, setGraphSize] = useState({ width: 0, height: 0, barSize: 25 });

    // 날짜 선택용 state
    const today = moment();
    const start14 = moment().subtract(13, 'days');
    const [startDate, setStartDate] = useState<Date>(
        moment().subtract(13, 'days').toDate()
    );
    const [endDate, setEndDate] = useState<Date>(new Date());

    const years = [moment().year() - 1, moment().year(), moment().year() + 1];
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const days = Array.from({ length: 31 }, (_, i) => i + 1);

    const emptyMonthlyData = Array.from({ length: 12 }, (_, i) => ({ month: `${i+1}월`, count: 0 }));
    const chartMonthlyData = monthlyData.length > 0 ? monthlyData : emptyMonthlyData;
        
    const SensorInfo = [
        { label: '이름', key: 'NAME' },
        { label: '아이디', key: 'SENSOR_ID' },
        { label: '구역', key: 'AREA' },
        { label: '이번 달 사용일수', key: 'USE_DAYS' },
        { label: '이번 달 사용시간', key: 'USE_HOURS' },
        { label: '센서 교체 횟수', key: 'SENSOR_COUNT' },
    ];

    const [weeklySize, setWeeklySize] = useState({ width: 0, height: 0 });
    const [monthlySize, setMonthlySize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        // -------------------------
        // 1️⃣ 센서 정보 fetch
        // -------------------------
        Data.fetchData(
            'sensor/getSensorAllListById',
            { SENSOR_ID: data.SENSOR_ID },
            (response: any) => {
                const arr = Array.isArray(response) ? response : Array.isArray(response.result) ? response.result : [];

                if (arr.length === 0) {
                    setInfo([]);
                    return;
                }

                const formatted = arr.map(row => ({
                    ...row,
                    BIRTH: row?.BIRTH ? moment(row.BIRTH).format('YYYY-MM-DD') : null
                }));

                setInfo(formatted);
            },
            true
        );

        // -------------------------
        // 2️⃣ 일간 데이터 fetch
        // -------------------------
        Data.fetchData(
            'sensor/DailyReplaceInd',
            { NAME: data?.NAME },
            (result: any, count: any) => {
                if (count > 0 && result.length > 0) {
                    const todayVal = result[0][7] ?? result[0]['7'] ?? 0;
                    const avgVal = result[0]['AVG'] || 0;
                    setDaily({ today: todayVal, avg: avgVal });

                    const weekArr: any[] = [];
                    for (let i = 1; i <= 7; i++) {
                        const dateLabel = moment().subtract(7 - i, 'days').format('MM/DD');
                        weekArr.push({ day: dateLabel, count: result[0][i] ?? 0 });
                    }
                    weekArr.push({ day: '평균', count: avgVal });
                    setWeeklyData(weekArr);
                } else {
                    setDaily({ today: 0, avg: 0 });
                    setWeeklyData([]);
                }
            },
            true
        );

        // -------------------------
        // 3️⃣ 월간 데이터 fetch
        // -------------------------
        Data.fetchData(
            'sensor/MonthlyReplaceInd',
            { NAME: data?.NAME },
            (result: any, count: any) => {
                if (count > 0 && result.length > 0) {
                    const monthlyObj = result[0];
                    const monthLabels = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];
                    const monthlyArr = monthLabels.map(label => ({ month: label, count: monthlyObj[label] ?? 0 }));
                    const avg = monthlyObj['AVG'] || 0;
                    monthlyArr.push({ month: '평균', count: avg });
                    setMonthlyData(monthlyArr);
                    setMonth({ avg });
                } else {
                    setMonth({ avg: 0 });
                    setMonthlyData([]);
                }
            },
            true
        );

        // -------------------------
        // 4️⃣ 연간 데이터 fetch
        // -------------------------
        Data.fetchData(
            'sensor/YearReplaceInd',
            { NAME: data?.NAME },
            (result: any, count: any) => {
                setYear(count > 0 && result.length > 0 ? result[0].TOTAL || 0 : 0);
            },
            true
        );

        // -------------------------
        // 5️⃣ 상단 14일 그래프 fetch
        // -------------------------
        const STARTDATE = moment().subtract(13, 'days');
        const ENDDATE = moment();
        Data.fetchData(
            'sensor/DailyReplacement',
            {
                NAME: data?.NAME,
                STARTDATE: STARTDATE.format('YYYY-MM-DD'),
                ENDDATE: ENDDATE.format('YYYY-MM-DD'),
            },
            (result: any[], count: number) => {
                const temp: any[] = [];
                for (let m = moment(STARTDATE); m.isSameOrBefore(ENDDATE); m.add(1, 'days')) {
                    const dateStr = m.format('YYYY-MM-DD');
                    const found = result.find(r => r.DATE === dateStr);
                    temp.push({ DATE: dateStr, COUNT: found ? found.COUNT : 0 });
                }
                setSearch(temp);
            },
            true
        );
    }, [isFocused]);

    // -------------------------
    // 날짜 검색 fetch
    // -------------------------
    const fetchData = () => {
        if (!startDate || !endDate) {
            alert("날짜를 선택해주세요.");
            return;
        }

        const STARTDATE = moment(startDate);
        const ENDDATE = moment(endDate);
        
        if (ENDDATE.isBefore(STARTDATE)) {
            alert("종료일은 시작일보다 빠를 수 없습니다.");
            return;
        }        
        const diffDays = ENDDATE.diff(STARTDATE, "days") + 1;

        if (diffDays > 31) {
            setTimeout(() => {
                alert("검색 기간은 최대 31일까지만 가능합니다.", [{ text: "확인" }]);
            }, 100);
            return;
        }

        Data.fetchData(
            "sensor/DailyReplacement",
            {
                NAME: data?.NAME,
                STARTDATE: STARTDATE.format("YYYY-MM-DD"),
                ENDDATE: ENDDATE.format("YYYY-MM-DD"),
            },
            (result: any[], count: number) => {
                const temp: any[] = [];
                for (let m = moment(STARTDATE); m.isSameOrBefore(ENDDATE); m.add(1, "days")) {
                    const dateStr = m.format("YYYY-MM-DD");
                    const found = result.find((r) => r.DATE === dateStr);
                    temp.push({ DATE: dateStr, COUNT: found ? found.COUNT : 0 });
                }
                setSearch(temp);

                // Bar size 조절
                let newBarSize = 25;
                if (diffDays > 14 && diffDays <= 20) {
                    setXTickFontSize(14); 
                    newBarSize = 22;
                } else if (diffDays > 20) {
                    setXTickFontSize(12);
                    newBarSize = 20;
                } else {
                    setXTickFontSize(16);
                }
                setGraphSize(prev => ({ ...prev, barSize: newBarSize }));
            },
            true
        );
    };

    return (
        <View style={styles.container}>
            <ScrollView contentContainerStyle={styles.scrollContent}>
                {/* 상단 카드 */}
                <View style={styles.statRow}>
                    <View style={[styles.statCard, { backgroundColor: '#E8F0FF' }]}>
                        <Text style={styles.statTitle}>금일 교체</Text>
                        <Text style={styles.statValue}>{daily?.today ?? '-'}</Text>
                    </View>
                    <View style={[styles.statCard, { backgroundColor: '#F1E6FF' }]}>
                        <Text style={styles.statTitle}>누적 교체 횟수</Text>
                        <Text style={styles.statValue}>{year ?? '-'}</Text>
                    </View>
                    <View style={[styles.statCard, { backgroundColor: '#FFF3E0' }]}>
                        <Text style={styles.statTitle}>주간 평균</Text>
                        <Text style={styles.statValue}>{daily?.avg ?? '-'}</Text>
                    </View>
                    <View style={[styles.statCard, { backgroundColor: '#E6FFEA' }]}>
                        <Text style={styles.statTitle}>월간 평균</Text>
                        <Text style={styles.statValue}>{month?.avg ?? '-'}</Text>
                    </View>
                </View>

                {/* 센서 정보 + 상단 그래프 */}
                <View style={[styles.rowContainer, { height: height * 0.36 }]}>
                <View style={styles.infoSection}>
                    {/* 센서 정보 제목 + 버튼을 한 줄에 배치 */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.sectionTitle}>센서 정보</Text>
                    <TouchableOpacity
                        onPress={() => { if (info && info.length > 0) navigation.navigate('SensorDetail', { data: info[0] }); }}
                    >
                        <Text style={styles.sectionTitle}>관리페이지로 이동</Text>
                    </TouchableOpacity>
                    </View>

                    <View style={styles.table}>
                    {SensorInfo.map((item, idx) => (
                        <View key={idx} style={styles.row}>
                        <Text style={styles.label}>{item.label}</Text>
                        <Text style={styles.value}>{data[item.key] || '-'}</Text>
                        </View>
                    ))}
                    </View>
                </View>
                    {/* 그래프 + 날짜검색 */}
                    <View style={[styles.graphSection, { flex: 5, marginLeft: 12 }]}
                        onLayout={(e) => { const { width, height } = e.nativeEvent.layout; setGraphSize(prev => ({ ...prev, width, height })); }}>

                        {/* 날짜 선택 한 줄 */}
                        <View style={styles.filterRow}>
                            <DatePicker
                                selected={startDate}
                                onChange={(date: Date) => setStartDate(date)}
                                dateFormat="yyyy-MM-dd"
                                portalId="datepicker-portal-root"
                                customInput={
                                    <View style={styles.dateInput}>
                                        <Text style={styles.dateText}>
                                            {moment(startDate).format('YYYY-MM-DD')}
                                        </Text>
                                    </View>
                                }
                            />

                            <Text style={styles.tilde}>~</Text>

                            <DatePicker
                                selected={endDate}
                                onChange={(date: Date) => setEndDate(date)}
                                dateFormat="yyyy-MM-dd"
                                portalId="datepicker-portal-root"
                                customInput={
                                    <View style={styles.dateInput}>
                                        <Text style={styles.dateText}>
                                            {moment(endDate).format('YYYY-MM-DD')}
                                        </Text>
                                    </View>
                                }
                            />

                            <TouchableOpacity style={styles.searchBoxRow} onPress={fetchData}>
                                <Text style={styles.searchText}>검색</Text>
                            </TouchableOpacity>
                        </View>

                        {search.length > 0 && graphSize.width > 0 && (
                            <BarChart
                                width={graphSize.width}
                                height={graphSize.height - 40}
                                data={search.map(item => ({ ...item, dayLabel: moment(item.DATE).format('MM/DD') }))}
                                margin={{ top: 30, right: 5, left: -30, bottom: 15 }}
                                barCategoryGap="10%"
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="dayLabel" interval={0} tick={{ fontSize: xTickFontSize }}/>
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="COUNT" fill="#8884d8"  barSize={graphSize.barSize || 25}>
                                    <LabelList position="top" dataKey="COUNT" />
                                </Bar>
                            </BarChart>
                        )}
                    </View>
                </View>

                    <View style={[styles.bottomGraphContainer, { height: height * 0.33 }]}>

                        {/* 주간 그래프 */}
                        <View
                            style={styles.bottomgraphSection}
                            onLayout={(e) => {
                                const { width, height } = e.nativeEvent.layout;
                                setWeeklySize({ width, height });
                            }}
                        >
                            <Text style={[styles.sectionTitle, { position: 'absolute', top: 16, left: 10 }]}>주간</Text>

                            {weeklyData.length > 0 && weeklySize.width > 0 && (
                                <BarChart
                                    width={weeklySize.width - 20}
                                    height={weeklySize.height - 40}
                                    data={weeklyData}
                                    margin={{ top: 30, right: 5, left: -25, bottom: -10 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="day" interval={0} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#8884d8" barSize={weeklySize.width / 12}>
                                        <LabelList dataKey="count" position="top" />
                                    </Bar>
                                </BarChart>
                            )}
                        </View>

                        {/* 월간 그래프 */}
                        <View
                            style={styles.bottomgraphSection}
                            onLayout={(e) => {
                                const { width, height } = e.nativeEvent.layout;
                                setMonthlySize({ width, height });
                            }}
                        >
                            <Text style={[styles.sectionTitle, { position: 'absolute', top: 16, left: 10 }]}>월간</Text>

                            {monthlySize.width > 0 && (
                            <BarChart
                                width={monthlySize.width - 20}
                                height={monthlySize.height - 40}
                                data={chartMonthlyData} // 항상 12개 month, count=0으로 채움
                                margin={{ top: 30, right: 5, left: -25, bottom: -10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" interval={0} />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#82ca9d" barSize={monthlySize.width / 20}>
                                <LabelList dataKey="count" position="top" />
                                </Bar>
                            </BarChart>
                            )}
                        </View>

                    </View>

            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    /* 기본 컨테이너 */
    container: { flex: 1, backgroundColor: '#fff' },
    scrollContent: { padding: 20 },

    /* 상단 통계 카드 */
    statRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 20
    },
    statCard: {
        flex: 1,
        marginHorizontal: 4,
        paddingVertical: 18,
        borderRadius: 12,
        alignItems: 'center'
    },
    statTitle: { fontSize: 14, color: '#444' },
    statValue: { fontSize: 24, fontWeight: '700', color: '#000', marginTop: 6 },

    /* 센서 정보 + 그래프 영역 */
    rowContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        marginBottom: 20
    },

    /* 센서 정보 박스 */
    infoSection: {
        flex: 2,
        marginRight: 10,
        padding: 15,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#eee',
        backgroundColor: '#fafafa',
        flexWrap: 'wrap',
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        marginBottom: 10,
        color: '#333'
    },
    table: { borderTopWidth: 1, borderColor: '#ddd' },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        position: 'relative',
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderColor: '#eee'
    },
    label: { width: 100, fontSize: 14, color: '#666' },
    value: {
        position: 'absolute',
        left: '50%',
        fontSize: 14,
        fontWeight: '500',
        color: '#000'
    },

    /* 그래프 영역 */
    graphSection: {
        flex: 5,
        padding: 15,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#ddd',
        backgroundColor: '#fff',
        alignItems: 'center'
    },

    /* 날짜 선택 */
    filterRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 10,
    },
    dateInput: {
        height: 36,
        minWidth: 130,
        paddingHorizontal: 14,
        borderRadius: 8,
        backgroundColor: '#f9f9f9',
        borderWidth: 1,
        borderColor: '#d1d5db',
        justifyContent: 'center',  
    },
    dateText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#111',
    },
    tilde: {
        fontSize: 16,
        fontWeight: '600',
        lineHeight: 36,             
    },
    searchBoxRow: {
        height: 32,
        paddingHorizontal: 12,
        borderRadius: 6,
        backgroundColor: '#8884d8',
        justifyContent: 'center',
        alignItems: 'center',
    },
    searchText: {
        fontSize: 12,
        color: '#fff',
        fontWeight: '600',
    },
    searchFont: {
        fontSize: 12,
        color: '#fff',
        fontWeight: '600'
    },

    /* 하단 그래프 */
    bottomGraphContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 15
    },
    bottomgraphSection: {
        flex: 1,
        marginHorizontal: 5,
        padding: 0,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#ddd',
        backgroundColor: '#fff',
        justifyContent: 'center',
        alignItems: 'center'
    },
});


